/**
 * Home screen versions (owner 2026-11-04): back toward today's cutiecaryn.com home - big Text / Gallery / Newsroom(→Inbox)
 * buttons + a row of small icon buttons + the mailbox prompt - but in the new design's colours, fonts and cards; no sticky
 * note. Chosen by ?home=<id> (remembered in this browser) or HOME_VARIANT in src/config/mode.ts. See HANDOFF.md §0.
 * Every button keeps the same destinations/rules as the rest of the app (Text and Vault go through the PIN).
 */
import { useMemo } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import { HOME_VARIANT, HOME_VARIANTS, FEATURES, type HomeVariantId } from '~/config/mode'
import type { GalleryImage } from '~/api/endpoints'
import { Icon } from '~/app/ui'

const KEY = 'cw-home-variant'
export function useHomeVariant(): HomeVariantId {
  return useMemo(() => {
    const isId = (v: string | null): v is HomeVariantId => !!v && (HOME_VARIANTS as readonly string[]).includes(v)
    const q = new URLSearchParams(location.search).get('home')
    try {
      if (isId(q)) { localStorage.setItem(KEY, q); return q }
      const saved = localStorage.getItem(KEY)
      if (isId(saved)) return saved
    } catch { /* private mode */ }
    return HOME_VARIANT
  }, [])
}

const LABELS: Record<HomeVariantId, string> = { familiar: 'Familiar', classic: 'Classic', 'classic-preview': 'Classic + preview', tiles: 'Cards + buttons', design: 'Design v7' }
/** Demo only: switch between the versions while reviewing. */
export function VariantPicker({ current }: { current: HomeVariantId }) {
  return (
    <nav className="v2-picker" aria-label="Home screen versions (demo)">
      <span>Home versions:</span>
      {HOME_VARIANTS.map((id) => (
        <a key={id} href={`/home?home=${id}`} aria-current={id === current ? 'true' : undefined}>{LABELS[id]}</a>
      ))}
    </nav>
  )
}

interface Props {
  variant: Exclude<HomeVariantId, 'design' | 'tiles'>
  unread: number; latest: string; loading: boolean; photos: GalleryImage[]; inboxNew: number
  hasAddress: boolean; onMailbox: () => void; expired: boolean
  go: (to: string) => void; navigate: NavigateFunction
}

export function HomeVariant(p: Props) {
  const lock = FEATURES.pinGate ? <Icon id="i-lock" className="icon v2-lock" /> : null
  const unread = p.unread > 0 ? <span className="v2-count" aria-label={`${p.unread} unread`}>{p.unread}</span> : null
  const newMail = p.inboxNew > 0 ? <span className="v2-dot" aria-label={`${p.inboxNew} new`} /> : null
  const expired = p.expired && (
    <button className="v2-expired" type="button" onClick={() => p.navigate('/settings/subscription?manage')}>
      <Icon id="i-clock" />Your access has ended · <b>Renew</b>
    </button>
  )
  const mailbox = FEATURES.mailboxPrompt && !p.hasAddress && (
    <button className="v2-mailbox" type="button" onClick={p.onMailbox}>
      <span className="v2-mailbox-ico"><Icon id="i-mail" /><i aria-hidden="true" /></span>
      <span className="grow"><b>You’ve got mail</b><small>Add your address so I can send you something</small></span>
      <Icon id="i-arrow" className="icon v2-arrow" />
    </button>
  )
  const smallTiles = [
    { label: 'Vault', icon: 'i-vault', to: '/vault', locked: true },
    ...(FEATURES.diary ? [{ label: 'Diary', icon: 'i-journal', to: '/diary' }] : []),
    { label: 'Wallet', icon: 'i-wallet', to: '/settings/wallet' },
    { label: 'Account', icon: 'i-user', to: '/settings/account' },
  ]
  const open = (t: { to: string; locked?: boolean }) => (t.locked ? p.go(t.to) : p.navigate(t.to))

  if (p.variant === 'familiar') {
    // today's cutiecaryn.com home, re-drawn in the new palette: one card, lowercase buttons with the icon on the right,
    // four icon squares, the mailbox popping out of the corner with "click me", the pumpkin underneath
    const row = (label: string, icon: string, onClick: () => void, extra?: React.ReactNode) => (
      <button className="v2-fam-btn" type="button" onClick={onClick}>
        <span className="v2-fam-label">{label}{extra}</span><Icon id={icon} className="icon v2-fam-ico" />
      </button>
    )
    return (
      <section className="v2-home v2-familiar" aria-label="Home">
        {expired}
        <div className="v2-fam-card">
          {FEATURES.mailboxPrompt && !p.hasAddress && (
            <button className="v2-fam-mailbox" type="button" onClick={p.onMailbox} aria-label="You have mail: add your mailing address">
              <span className="v2-mailbox-ico"><Icon id="i-mail" /><i aria-hidden="true" /></span><small>click me ↑</small>
            </button>
          )}
          {row('text', 'i-chat', () => p.go('/text'), <>{unread}{lock}</>)}
          {row('gallery', 'i-image', () => p.navigate('/gallery'))}
          {row('email', 'i-mail', () => p.navigate('/inbox'), newMail)}
          <div className="v2-fam-icons">
            {smallTiles.map((t) => (
              <button key={t.label} className="v2-fam-sq" type="button" onClick={() => open(t)} aria-label={t.label} title={t.label}>
                <Icon id={t.icon} />
              </button>
            ))}
          </div>
        </div>
        <img className="v2-fam-pumpkin" src="/design/pumpkin.webp" alt="" aria-hidden="true" width={640} height={529} />
      </section>
    )
  }

  const preview = p.variant === 'classic-preview'
  return (
    <section className={`v2-home v2-classic${preview ? ' preview' : ''}`} aria-label="Home">
      {expired}
      <div className="v2-stack">
        <button className="v2-big dark" type="button" onClick={() => p.go('/text')}>
          <Icon id="i-chat" className="icon v2-big-ico" />
          <span className="grow"><b>Text</b><small className="v2-teaser">Private messages</small></span>{/* owner 11-04: no message preview - "Private messages" */}
          {unread}{lock}
        </button>
        <button className="v2-big rose" type="button" onClick={() => p.navigate('/gallery')}>
          <Icon id="i-image" className="icon v2-big-ico" />
          <span className="grow"><b>Gallery</b>{preview && <small className="v2-teaser">New moments to explore</small>}</span>
          {preview && <span className="v2-thumbs" aria-hidden="true">{p.photos.slice(0, 3).map((g) => g.urls.thumbnail && <img key={g.id} src={g.urls.thumbnail} alt="" />)}</span>}
          {!preview && <Icon id="i-arrow" className="icon v2-arrow" />}
        </button>
        <button className="v2-big light" type="button" onClick={() => p.navigate('/inbox')}>
          <Icon id="i-mail" className="icon v2-big-ico" />
          <span className="grow"><b>Email</b>{preview && <small className="v2-teaser">{p.inboxNew > 0 ? `${p.inboxNew} new from me` : 'Letters from me'}</small>}</span>
          {newMail}<Icon id="i-arrow" className="icon v2-arrow" />
        </button>
      </div>
      <div className="v2-icons">
        {smallTiles.map((t) => (
          <button key={t.label} className="v2-icon" type="button" onClick={() => open(t)} aria-label={t.label}>
            <Icon id={t.icon} />
            <small>{t.label}</small>
          </button>
        ))}
      </div>
      {mailbox}
    </section>
  )
}

/**
 * Version 4 ("tiles", owner 2026-11-04): the original Texts + Gallery cards stay as designed; every other button sits
 * underneath as ONE row of equal buttons (same size, same style): Inbox, Vault (PIN), Diary, Wallet, Account.
 */
export function ButtonRow({ inboxNew, go, navigate }: { inboxNew: number; go: (to: string) => void; navigate: NavigateFunction }) {
  const items = [
    { label: 'Email', icon: 'i-mail', to: '/inbox', dot: inboxNew > 0, locked: false },
    { label: 'Vault', icon: 'i-vault', to: '/vault', dot: false, locked: true },
    ...(FEATURES.diary ? [{ label: 'Diary', icon: 'i-journal', to: '/diary', dot: false, locked: false }] : []),
    { label: 'Wallet', icon: 'i-wallet', to: '/settings/wallet', dot: false, locked: false },
    { label: 'Account', icon: 'i-user', to: '/settings/account', dot: false, locked: false },
  ]
  return (
    <nav className="v2-btnrow" aria-label="More">
      {items.map((t) => (
        <button key={t.label} className="v2-rowbtn" type="button" onClick={() => (t.locked ? go(t.to) : navigate(t.to))}>
          <span className="v2-rowbtn-ico"><Icon id={t.icon} />{t.dot && <span className="v2-dot" aria-label="new" />}</span>
          <small>{t.label}</small>
        </button>
      ))}
    </nav>
  )
}
